import { Phone, Mail, Clock, MapPin, Globe, MessageCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import PetrocontrolLogo from "@/components/PetrocontrolLogo";

export default function ContactSection() {
  const { t } = useLanguage();
  const settings = useSiteSettings();
  const section = t("contact");

  const phoneClean = settings.phone.replace(/[^+\d]/g, "");
  const waClean = settings.whatsapp.replace(/[^\d]/g, "");

  return (
    <section id="contact" data-testid="contact-section" aria-label="Contacto PETROCONTROL" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-16">
            <div>
              <p className="text-[#B59A65] text-xs uppercase tracking-[0.3em] mb-3 font-semibold">{section.subtitle}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#152238] leading-[1.1] tracking-[-0.02em]">{section.headline}</h2>
            </div>
            <div className="flex items-end">
              <p className="text-[#66727B] text-base leading-relaxed">{section.description}</p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal animation="reveal-left">
            <div className="bg-[#faf8f4] rounded-2xl p-8 md:p-10 h-full" data-testid="contact-info">
              <div className="mb-8">
                <PetrocontrolLogo className="h-9" />
              </div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-[#103F38]" /></div>
                  <div>
                    <p className="font-medium text-[#152238] text-sm">{section.officeAddress}</p>
                    <p className="text-[#66727B] text-sm">{settings.address_line1}<br />{settings.address_line2}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><Phone className="h-5 w-5 text-[#103F38]" /></div>
                  <div>
                    <p className="font-medium text-[#152238] text-sm">{section.phone}</p>
                    <a href={`tel:${phoneClean}`} data-testid="contact-phone" className="text-[#103F38] text-sm font-medium hover:underline">{settings.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><MessageCircle className="h-5 w-5 text-[#103F38]" /></div>
                  <div>
                    <p className="font-medium text-[#152238] text-sm">{section.whatsapp}</p>
                    <a href={`https://api.whatsapp.com/send?phone=${waClean}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="text-[#103F38] text-sm font-medium hover:underline">{section.whatsappCta}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-[#103F38]" /></div>
                  <div>
                    <p className="font-medium text-[#152238] text-sm">{section.emailLabel}</p>
                    <a href={`mailto:${settings.email}`} data-testid="contact-email" className="text-[#103F38] text-sm font-medium hover:underline">{settings.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><Clock className="h-5 w-5 text-[#103F38]" /></div>
                  <div>
                    <p className="font-medium text-[#152238] text-sm">{section.officeHours}</p>
                    <p className="text-[#66727B] text-sm">{t("consultation.weekdays")}</p>
                    <p className="text-[#66727B] text-sm">{t("consultation.saturday")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><Globe className="h-5 w-5 text-[#103F38]" /></div>
                  <div>
                    <p className="font-medium text-[#152238] text-sm mb-2">{section.languages}</p>
                    <div className="flex flex-wrap gap-2">
                      {(section.langList || []).map((lang) => (
                        <Badge key={lang} variant="outline" className="rounded-full border-[#d5d8d3] text-[#152238] text-xs font-medium">{lang}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal animation="reveal-right">
            <div data-testid="map-placeholder" className="rounded-2xl overflow-hidden h-full min-h-[480px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3732.5892!2d-103.3947!3d20.6847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8428ae4e5b3e0001%3A0x1!2sAv.%20Juan%20Palomar%20y%20Arias%20567%2C%20Monraz%2C%2044670%20Guadalajara%2C%20Jal.!5e0!3m2!1ses!2smx!4v1700000000000!5m2!1ses!2smx"
                width="100%" height="100%" style={{ border: 0, minHeight: "480px" }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Oficina PETROCONTROL Guadalajara"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
